package com.fantasytenis.shared.data.survivor.repository

import com.fantasytenis.shared.core.network.ApiResponse
import com.fantasytenis.shared.data.survivor.api.SurvivorApi
import com.fantasytenis.shared.data.survivor.dto.*
import com.fantasytenis.shared.domain.bracket.entity.LockOverride
import com.fantasytenis.shared.domain.bracket.entity.RoundName
import com.fantasytenis.shared.domain.survivor.entity.*
import kotlinx.serialization.json.Json

/**
 * Leaderboard result with metadata.
 */
data class SurvivorLeaderboardResult(
    val entries: List<SurvivorEntryEntity>,
    val total: Int,
    val aliveCount: Int
)

/**
 * Survivor Repository Interface.
 *
 * Defines the contract for survivor contest data operations.
 */
interface SurvivorRepository {
    // Survivor Contests
    suspend fun getSurvivorContest(contestId: String): Result<SurvivorContestEntity>
    suspend fun listSurvivorContests(
        status: Int? = null,
        tournamentId: String? = null,
        featuredOnly: Boolean = false,
        limit: Int = 20,
        offset: Int = 0
    ): Result<List<SurvivorContestEntity>>

    // Survivor Entries
    suspend fun createSurvivorEntry(contestId: String): Result<SurvivorEntryEntity>
    suspend fun getSurvivorEntry(entryId: String): Result<SurvivorEntryWithPicksEntity>
    suspend fun getUserSurvivorEntries(contestId: String? = null): Result<List<SurvivorEntryEntity>>
    suspend fun rebuySurvivorEntry(entryId: String): Result<SurvivorEntryEntity>
    suspend fun cancelSurvivorEntry(entryId: String): Result<SurvivorEntryEntity>

    // Picks
    suspend fun getAvailablePlayers(entryId: String, round: Int? = null): Result<List<AvailablePlayerEntity>>
    suspend fun makeSurvivorPick(entryId: String, playerId: String): Result<SurvivorPickEntity>
    suspend fun updateSurvivorPick(entryId: String, pickId: String, playerId: String): Result<SurvivorPickEntity>

    // Leaderboard & Round Info
    suspend fun getSurvivorLeaderboard(
        contestId: String,
        statusFilter: String? = null,
        limit: Int = 100,
        offset: Int = 0
    ): Result<SurvivorLeaderboardResult>
    suspend fun getSurvivorRoundSummary(contestId: String): Result<List<SurvivorRoundSummaryEntity>>
}

/**
 * Survivor Repository Implementation.
 */
class SurvivorRepositoryImpl(
    private val survivorApi: SurvivorApi
) : SurvivorRepository {

    private val json = Json { ignoreUnknownKeys = true }

    // ===== SURVIVOR CONTESTS =====

    override suspend fun getSurvivorContest(contestId: String): Result<SurvivorContestEntity> {
        return when (val response = survivorApi.getSurvivorContest(contestId)) {
            is ApiResponse.Success -> Result.success(response.data.toEntity())
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun listSurvivorContests(
        status: Int?,
        tournamentId: String?,
        featuredOnly: Boolean,
        limit: Int,
        offset: Int
    ): Result<List<SurvivorContestEntity>> {
        return when (val response = survivorApi.listSurvivorContests(status, tournamentId, featuredOnly, limit, offset)) {
            is ApiResponse.Success -> Result.success(response.data.map { it.toEntity() })
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    // ===== SURVIVOR ENTRIES =====

    override suspend fun createSurvivorEntry(contestId: String): Result<SurvivorEntryEntity> {
        return when (val response = survivorApi.createSurvivorEntry(contestId)) {
            is ApiResponse.Success -> Result.success(response.data.toEntity())
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun getSurvivorEntry(entryId: String): Result<SurvivorEntryWithPicksEntity> {
        return when (val response = survivorApi.getSurvivorEntry(entryId)) {
            is ApiResponse.Success -> {
                val data = response.data
                if (data.data != null) {
                    Result.success(
                        SurvivorEntryWithPicksEntity(
                            entry = data.data.toEntity(),
                            picks = data.picks?.map { it.toEntity() } ?: emptyList()
                        )
                    )
                } else {
                    Result.failure(Exception("No entry data returned"))
                }
            }
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun getUserSurvivorEntries(contestId: String?): Result<List<SurvivorEntryEntity>> {
        return when (val response = survivorApi.getUserSurvivorEntries(contestId)) {
            is ApiResponse.Success -> Result.success(response.data.map { it.toEntity() })
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun rebuySurvivorEntry(entryId: String): Result<SurvivorEntryEntity> {
        return when (val response = survivorApi.rebuySurvivorEntry(entryId)) {
            is ApiResponse.Success -> Result.success(response.data.toEntity())
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun cancelSurvivorEntry(entryId: String): Result<SurvivorEntryEntity> {
        return when (val response = survivorApi.cancelSurvivorEntry(entryId)) {
            is ApiResponse.Success -> Result.success(response.data.toEntity())
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    // ===== PICKS =====

    override suspend fun getAvailablePlayers(entryId: String, round: Int?): Result<List<AvailablePlayerEntity>> {
        return when (val response = survivorApi.getAvailablePlayers(entryId, round)) {
            is ApiResponse.Success -> Result.success(response.data.map { it.toEntity() })
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun makeSurvivorPick(entryId: String, playerId: String): Result<SurvivorPickEntity> {
        val request = MakeSurvivorPickRequest(playerId = playerId)

        return when (val response = survivorApi.makeSurvivorPick(entryId, request)) {
            is ApiResponse.Success -> Result.success(response.data.toEntity())
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun updateSurvivorPick(entryId: String, pickId: String, playerId: String): Result<SurvivorPickEntity> {
        val request = UpdateSurvivorPickRequest(playerId = playerId)

        return when (val response = survivorApi.updateSurvivorPick(entryId, pickId, request)) {
            is ApiResponse.Success -> Result.success(response.data.toEntity())
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    // ===== LEADERBOARD & ROUND INFO =====

    override suspend fun getSurvivorLeaderboard(
        contestId: String,
        statusFilter: String?,
        limit: Int,
        offset: Int
    ): Result<SurvivorLeaderboardResult> {
        return when (val response = survivorApi.getSurvivorLeaderboard(contestId, statusFilter, limit, offset)) {
            is ApiResponse.Success -> {
                Result.success(
                    SurvivorLeaderboardResult(
                        entries = response.data.data?.map { it.toEntity() } ?: emptyList(),
                        total = response.data.total,
                        aliveCount = response.data.aliveCount
                    )
                )
            }
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }

    override suspend fun getSurvivorRoundSummary(contestId: String): Result<List<SurvivorRoundSummaryEntity>> {
        return when (val response = survivorApi.getSurvivorRoundSummary(contestId)) {
            is ApiResponse.Success -> Result.success(response.data.map { it.toEntity() })
            is ApiResponse.Error -> Result.failure(Exception(response.message))
        }
    }
}

// ===== DTO TO ENTITY MAPPERS =====

private fun SurvivorContestDto.toEntity(): SurvivorContestEntity {
    val json = Json { ignoreUnknownKeys = true }

    val parsedPayoutStructure = payoutStructure?.let {
        try {
            val dtos = json.decodeFromString<List<SurvivorPayoutTierDto>>(it)
            dtos.map { tier ->
                SurvivorPayoutTierEntity(
                    minRank = tier.minRank,
                    maxRank = tier.maxRank,
                    amount = tier.amount,
                    percentage = tier.percentage,
                    description = tier.description
                )
            }
        } catch (e: Exception) {
            emptyList()
        }
    } ?: emptyList()

    return SurvivorContestEntity(
        id = id,
        name = name,
        description = description,
        drawId = drawId,
        tournamentName = tournamentName,
        variation = SurvivorVariation.fromApiValue(variation),
        entryFee = entryFee,
        prizePool = prizePool,
        maxEntries = maxEntries,
        currentEntries = currentEntries,
        aliveEntries = aliveEntries,
        maxEntriesPerUser = maxEntriesPerUser,
        currentRound = currentRound?.let { RoundName.fromApiValue(it) },
        roundDeadline = roundDeadline,
        status = SurvivorContestStatus.fromApiValue(status),
        strikeLimit = strikeLimit,
        rebuyDeadlineRound = rebuyDeadlineRound?.let { RoundName.fromApiValue(it) },
        rebuyFee = rebuyFee,
        payoutStructure = parsedPayoutStructure,
        isFeatured = isFeatured,
        createdAt = createdAt,
        lockOverride = LockOverride.fromApiValue(lockOverride),
        lockOverrideBy = lockOverrideBy.ifBlank { null },
        lockOverrideAt = lockOverrideAt.ifBlank { null },
        lockOverrideReason = lockOverrideReason.ifBlank { null }
    )
}

private fun SurvivorEntryDto.toEntity(): SurvivorEntryEntity {
    return SurvivorEntryEntity(
        id = id,
        contestId = contestId,
        userId = userId,
        userName = userName,
        status = SurvivorEntryStatus.fromApiValue(status),
        strikesUsed = strikesUsed,
        survivedRounds = survivedRounds,
        eliminatedInRound = eliminatedInRound?.let { RoundName.fromApiValue(it) },
        eliminatedByMatchId = eliminatedByMatchId,
        rank = rank,
        payoutAmount = payoutAmount,
        rebuyCount = rebuyCount,
        lastPickRound = lastPickRound?.let { RoundName.fromApiValue(it) },
        createdAt = createdAt,
        eliminatedAt = eliminatedAt
    )
}

private fun SurvivorPickDto.toEntity(): SurvivorPickEntity {
    return SurvivorPickEntity(
        id = id,
        entryId = entryId,
        round = RoundName.fromApiValue(round) ?: RoundName.R32,
        playerId = playerId,
        playerName = playerName,
        playerSeed = playerSeed,
        playerCountry = playerCountry,
        opponentId = opponentId,
        opponentName = opponentName,
        opponentSeed = opponentSeed,
        matchId = matchId,
        matchTime = matchTime,
        result = SurvivorPickResult.fromApiValue(result),
        matchScore = matchScore,
        pickedAt = pickedAt,
        resolvedAt = resolvedAt
    )
}

private fun AvailablePlayerDto.toEntity(): AvailablePlayerEntity {
    return AvailablePlayerEntity(
        playerId = playerId,
        playerName = playerName,
        seed = seed,
        country = country,
        atpRanking = atpRanking,
        matchId = matchId,
        opponentId = opponentId,
        opponentName = opponentName,
        opponentSeed = opponentSeed,
        matchTime = matchTime,
        court = court,
        ownershipPercentage = ownershipPercentage,
        alreadyUsed = alreadyUsed,
        isMatchLocked = isMatchLocked
    )
}

private fun SurvivorRoundSummaryDto.toEntity(): SurvivorRoundSummaryEntity {
    return SurvivorRoundSummaryEntity(
        round = RoundName.fromApiValue(round) ?: RoundName.R32,
        roundStatus = roundStatus,
        deadline = deadline,
        totalEntries = totalEntries,
        aliveAfter = aliveAfter,
        eliminatedCount = eliminatedCount,
        matchesCompleted = matchesCompleted,
        totalMatches = totalMatches
    )
}
